"use client"

// src/components/pages/ProfilePage.jsx
import { useState } from "react"
import DashboardTemplate from "../templates/DashboardTemplate"
import { useAuth } from "../../hooks/useAuth"
import Button from "../atoms/Button"
import Input from "../atoms/Input"

const ProfilePage = () => {
  const { user, logout, updateUser } = useAuth()
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    username: user?.username || "",
    email: user?.email || "",
    phone: user?.phone || "",
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Determine the avatar initial with a fallback
  const avatarInitial = user && user.username ? user.username.charAt(0).toUpperCase() : "?"

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      // Simulate API call to update user profile
      // In a real app, you would call your API here
      // const response = await api.put(`/users/${user._id}`, formData);

      // For now, just update the local state after a delay
      await new Promise((resolve) => setTimeout(resolve, 800))

      // Update user context with new data
      if (updateUser) {
        updateUser({
          ...user,
          ...formData,
        })
      }

      setSuccess("Profile updated successfully!")
      setIsEditing(false)
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update profile")
    } finally {
      setLoading(false)
    }
  }

  return (
    <DashboardTemplate>
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg border border-amber-200">
          <div className="p-6 border-b border-amber-200">
            <h1 className="text-3xl font-bold text-amber-800">My Profile</h1>
          </div>

          {error && (
            <div className="mx-6 mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">{error}</div>
          )}

          {success && (
            <div className="mx-6 mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
              {success}
            </div>
          )}

          {user ? (
            <div className="p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-6 mb-8">
                {/* Avatar Section */}
                <div className="flex flex-col items-center">
                  {user.avatar ? (
                    <img
                      src={user.avatar || "/placeholder.svg"}
                      alt="Profile"
                      className="w-24 h-24 rounded-full object-cover border-4 border-amber-300 shadow-md"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-amber-100 flex items-center justify-center border-4 border-amber-300 shadow-md">
                      <span className="text-3xl font-bold text-amber-800">{avatarInitial}</span>
                    </div>
                  )}
                  <p className="mt-2 text-amber-700 font-medium">{user.role || "Customer"}</p>
                </div>

                {/* User Info Section */}
                <div className="flex-1">
                  {isEditing ? (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="username" className="block mb-1 font-medium text-amber-700">
                          Username
                        </label>
                        <Input
                          id="username"
                          name="username"
                          value={formData.username}
                          onChange={handleChange}
                          className="border-amber-300 focus:ring-amber-500 focus:border-amber-500"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block mb-1 font-medium text-amber-700">
                          Email
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="border-amber-300 focus:ring-amber-500 focus:border-amber-500"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block mb-1 font-medium text-amber-700">
                          Phone
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="border-amber-300 focus:ring-amber-500 focus:border-amber-500"
                        />
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button type="submit" disabled={loading} className="bg-amber-600 hover:bg-amber-700 text-white">
                          {loading ? "Saving..." : "Save Changes"}
                        </Button>
                        <Button
                          type="button"
                          onClick={() => setIsEditing(false)}
                          className="bg-gray-200 hover:bg-gray-300 text-gray-800"
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <h2 className="text-lg font-semibold text-amber-800">{user.username || "Username not set"}</h2>
                        <p className="text-gray-600">{user.email || "Email not set"}</p>
                        {user.phone && <p className="text-gray-600">{user.phone}</p>}
                      </div>

                      <div>
                        <Button
                          onClick={() => setIsEditing(true)}
                          className="bg-amber-500 hover:bg-amber-600 text-white"
                        >
                          Edit Profile
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Account Actions */}
              <div className="border-t border-amber-200 pt-6 mt-6">
                <h3 className="text-lg font-semibold text-amber-800 mb-4">Account Actions</h3>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={logout} className="bg-red-500 hover:bg-red-600 text-white">
                    Logout
                  </Button>
                  {user.role !== "owner" && (
                    <Button
                      onClick={() => (window.location.href = "/payment")}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      Upgrade to Owner
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6 text-center">
              <p className="text-gray-600 mb-4">Please log in to view your profile.</p>
              <Button
                onClick={() => (window.location.href = "/login")}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                Login
              </Button>
            </div>
          )}
        </div>
      </div>
    </DashboardTemplate>
  )
}

export default ProfilePage
